import { create } from 'zustand';

export interface AppErrorEntry {
  /** Stable id per toast — used as React key + dismissal handle. */
  readonly id: number;
  /** Human-readable message; comes from the server's `errorMessage` when present. */
  readonly message: string;
  /** Backend status code (e.g. `4XX`, `403`, `500`, `NETWORK`). */
  readonly code?: string;
  /** Optional title override; defaults to a sensible label per code. */
  readonly title?: string;
}

interface AppErrorState {
  errors: readonly AppErrorEntry[];
  /** Push a new error toast. Returns the assigned id. */
  pushError: (input: { message: string; code?: string; title?: string }) => number;
  /** Dismiss a single toast by id. */
  dismiss: (id: number) => void;
  /** Clear every queued error. */
  clear: () => void;
}

let nextId = 1;

export const useAppErrorStore = create<AppErrorState>((set) => ({
  errors: [],
  pushError: ({ message, code, title }) => {
    let resultId = -1;
    set((state) => {
      const duplicate = state.errors.find(
        (error) => error.message === message && error.code === code && error.title === title,
      );
      if (duplicate) {
        resultId = duplicate.id;
        return state;
      }

      resultId = nextId++;
      return { errors: [...state.errors, { id: resultId, message, code, title }] };
    });
    return resultId;
  },
  dismiss: (id) => set((state) => ({ errors: state.errors.filter((e) => e.id !== id) })),
  clear: () => set({ errors: [] }),
}));

/**
 * Imperative helper for non-React modules (e.g. axios interceptors). Mirrors
 * the React hook's `pushError` action.
 */
export function reportAppError(input: { message: string; code?: string; title?: string }): number {
  return useAppErrorStore.getState().pushError(input);
}

/**
 * Mark a request to be excluded from the global error toast. Used by callers
 * that already render their own inline error UI (e.g. login form, change
 * password modal) so the user doesn't see two duplicate dialogs.
 *
 * Pass the resulting object as the `meta` field on an axios request config:
 *
 *   api.post(url, body, { ...suppressGlobalErrorToast() })
 */
export function suppressGlobalErrorToast(): { meta: { suppressGlobalErrorToast: true } } {
  return { meta: { suppressGlobalErrorToast: true } };
}
